import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Panel,
  PanelGroup,
  PanelResizeHandle,
  type ImperativePanelHandle,
} from 'react-resizable-panels';
import { toast } from 'sonner';
import { CodeEditor } from '@kodprep/platform-ui/organisms';
import { useEditorStore } from '@/stores/editorStore';
import { usePreviewStore } from '@/stores/previewStore';
import { useProblem } from '@/lib/hooks/useProblems';
import { useTestCases } from '@/lib/hooks/useTestCases';
import { useSubmission } from '@/lib/hooks/useSubmission';
import { useInvalidateOnSolved } from '@/lib/hooks/useInvalidateOnSolved';
import { useEditorialAccess } from '@/lib/hooks/useEditorialAccess';
import { useProblemSubmissions } from '@/lib/hooks/useProblemSubmissions';
import { useProblemSequence } from '@/lib/hooks/useProblemSequence';
import { useSolveTelemetry } from '@/lib/hooks/useSolveTelemetry';
import { ExplainSolveDialog } from './ExplainSolveDialog';
import { AskLeetsPanel } from './AskLeetsPanel';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { LeftPanel, type TabKey } from './LeftPanel';
import { NextInSequence } from './NextInSequence';
import { ProblemTopBar } from './ProblemTopBar';
import { EditorToolbar } from './EditorToolbar';
import { EditorStatusBar } from './EditorStatusBar';
import { TestCasePanel } from './TestCasePanel';
import { Code2, History, Sparkles, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { resolveAvailableLanguages, resolvePreferredLanguage } from './languages';
import { resolveStarter, isAutoStarter } from './starter';
import { typedCharsFrom } from './telemetry';
import { SubmissionDetail, VERDICT_LABEL, verdictColorClass } from './SubmissionDetail';
import { PromptEditorPanel } from './PromptEditorPanel';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

interface Props {
  slug: string;
  topicId?: string;
  batchId?: string;
  hackathonComponentId?: string;
}

export function ProblemWorkspace({ slug, topicId, batchId, hackathonComponentId }: Props) {
  const { data: problem, isLoading } = useProblem(slug);
  const { data: rawTestCases } = useTestCases(slug);
  const testCases = rawTestCases ?? [];
  const { language, setLanguage, theme, fontSize, tabSize, getCode, setCode } = useEditorStore();
  const { run, submit, result, isJudging, mode } = useSubmission(slug, hackathonComponentId);
  useInvalidateOnSolved(result, topicId);

  // Lifted out of LeftPanel so the accepted-solve panel — which lives in the
  // other pane — can send the student to the editorial it just unlocked.
  const [leftTab, setLeftTab] = useState<TabKey>('description');
  const sequence = useProblemSequence(slug, { topicId, batchId });

  // Records HOW the problem is being solved, not just whether it was. A trainer
  // running a lab needs to tell a student who worked through a problem from one
  // who watched a solution arrive whole — the passed/failed record cannot.
  //
  // Off during trainer preview: a trainer trying a problem as a student must not
  // accrue solve evidence under their own name, nor surface on a lab board.
  const previewActive = usePreviewStore((s) => s.active);
  const getCodeChars = useCallback(
    () => getCode(slug, language).length,
    [getCode, slug, language],
  );
  const telemetry = useSolveTelemetry({
    slug,
    language,
    getCodeChars,
    enabled: !previewActive,
  });

  const [cursorPos, setCursorPos] = useState({ line: 1, col: 1 });
  const [maximized, setMaximized] = useState<'description' | 'editor' | 'testcase' | null>(null);
  const [testcaseCollapsed, setTestcaseCollapsed] = useState(false);
  const [editorCollapsed, setEditorCollapsed] = useState(false);
  const [tutorCollapsed, setTutorCollapsed] = useState(false);
  const testcaseRef = useRef<ImperativePanelHandle>(null);
  const editorRef = useRef<ImperativePanelHandle>(null);
  const tutorRef = useRef<ImperativePanelHandle>(null);
  const [mobileTab, setMobileTab] = useState<'description' | 'code'>('description');
  const isMobile = useIsMobile();

  // "Nudge" in-workspace AI tutor chat. Deliberately not gated on
  // acceptedSubmit (unlike ExplainSolveDialog below) — it's meant to help
  // *during* solving, before any submission exists.
  const [tutorOpen, setTutorOpen] = useState(false);

  // A submission opened from the Submissions list shows in the right panel as a
  // "Code | <verdict>" tab (LeetCode-style), alongside the editor.
  const [openSub, setOpenSub] = useState<{ id: string; verdict: string } | null>(null);
  const [rightView, setRightView] = useState<'code' | 'submission'>('code');
  const openSubmission = useCallback((id: string, verdict: string) => {
    setOpenSub({ id, verdict });
    setRightView('submission');
    setMobileTab('code'); // on mobile the detail lives in the code pane
  }, []);
  const closeSubmission = useCallback(() => {
    setOpenSub(null);
    setRightView('code');
  }, []);

  // Collapse the bottom testcase/console panel while viewing a submission (the
  // detail takes the full right pane, LeetCode-style); restore it on return.
  useEffect(() => {
    const panel = testcaseRef.current;
    if (!panel) return;
    if (rightView === 'submission') panel.collapse();
    else panel.expand();
  }, [rightView]);

  const toggleMax = useCallback(
    (pane: 'description' | 'editor' | 'testcase') =>
      setMaximized((m) => (m === pane ? null : pane)),
    [],
  );
  const togglePanel = useCallback((panel: ImperativePanelHandle | null) => {
    if (!panel) return;
    if (panel.isCollapsed()) panel.expand();
    else panel.collapse();
  }, []);

  // Load starter code if no saved code exists
  // Wait for problem to load before setting code — otherwise generic template
  // gets saved before the API returns problem-specific starter code
  const availableLanguages = resolveAvailableLanguages(problem);

  // The selected language is persisted globally (not per problem), so it can
  // easily be one this problem doesn't support — e.g. sql carried over from a
  // previously-solved SQL problem onto an ordinary stdio problem, or a
  // function-mode problem that lacks a stub for the persisted language. Reset
  // to this problem's default whenever the current selection isn't available.
  // Wait for the problem to load first, otherwise a SQL problem would briefly
  // reset away from sql (undefined resolves to the stdio list) and back again.
  // A problem that authors starter code only for other languages (a
  // JavaScript-course problem) also switches to its authored language.
  useEffect(() => {
    if (!problem || availableLanguages.length === 0) return;
    const preferred = resolvePreferredLanguage(problem, availableLanguages, language);
    if (preferred !== language) setLanguage(preferred);
    // Keyed on the problem only: re-running when `language` changes would
    // undo a student's deliberate switch away from the authored language.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [problem]);

  // Still guard against a selection this problem cannot run at all.
  useEffect(() => {
    if (!problem || availableLanguages.length === 0) return;
    if (!availableLanguages.some((l) => l.value === language)) {
      setLanguage(availableLanguages[0].value);
    }
  }, [problem, language, availableLanguages, setLanguage]);

  // Use saved code, but regenerate when it's empty or just an unedited default
  // template — otherwise stale stdio boilerplate saved before a stdio->function
  // conversion would permanently mask the generated function stub.
  let code = getCode(slug, language);
  if (problem && isAutoStarter(code)) {
    const starter = resolveStarter(problem, language);
    if (starter && starter !== code) {
      code = starter;
      setCode(slug, language, code);
    }
  }

  // Guard both the top-bar buttons and the Cmd/Ctrl+Enter shortcuts: trainers
  // previewing as a student must never create runs/submissions. Read the store
  // imperatively so the keyboard-shortcut effect needs no re-registration.
  const handleRun = useCallback(() => {
    if (usePreviewStore.getState().active) {
      toast.error('Not available in student preview', { id: 'preview-block' });
      return;
    }
    telemetry.recordRun();
    run(getCode(slug, language), language);
  }, [run, getCode, slug, language, telemetry]);

  const handleSubmit = useCallback(() => {
    if (usePreviewStore.getState().active) {
      toast.error('Not available in student preview', { id: 'preview-block' });
      return;
    }
    telemetry.recordSubmit();
    submit(getCode(slug, language), language);
  }, [submit, getCode, slug, language, telemetry]);

  // Ask the server to confirm the solve once a submission comes back accepted.
  // Only for `submit` — a "run" verdict reflects the sample cases alone, and
  // treating it as a solve would mark problems solved that are not.
  const acceptedSubmit = mode === 'submit' && result?.verdict === 'AC';
  const { recordSolved } = telemetry;
  useEffect(() => {
    if (acceptedSubmit) recordSolved();
  }, [acceptedSubmit, recordSolved]);

  // Same earn-it rule as ExplainSolveDialog below, extended to cover a solve
  // from an earlier session and the staff roles the API lets through. Drives
  // whether the Editorial tab exists at all — and, through it, whether the
  // editorial is ever requested. See useEditorialAccess.
  const editorialUnlocked = useEditorialAccess(slug, acceptedSubmit);

  // Solved at any point, not just in this session. Shares its query key with
  // useEditorialAccess above, so this costs no extra request.
  //
  // No staff branch, deliberately: a trainer who never submitted has no code
  // of their own to be asked about, and the server returns 204 for them.
  const { data: submissions } = useProblemSubmissions(slug);
  const solvedEver = acceptedSubmit || (submissions ?? []).some((s) => s.verdict === 'AC');

  // Sits under the accepted verdict. The comprehension dialog opens over the
  // top of it on the same event; that is deliberate — the dialog is answered
  // and dismissed first, and this is what the student finds underneath,
  // rather than two things competing for the same moment.
  const nextInSequenceEl = (
    <NextInSequence
      seq={sequence}
      origin={{ topicId, batchId }}
      onReadEditorial={editorialUnlocked ? () => setLeftTab('editorial') : undefined}
    />
  );


  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key === 'Enter' && e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      } else if (mod && e.key === 'Enter') {
        e.preventDefault();
        handleRun();
      } else if (mod && e.key === 's') {
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleRun, handleSubmit]);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        Loading problem...
      </div>
    );
  }

  if (!problem) {
    return (
      <div className="flex h-full items-center justify-center text-destructive">
        Problem not found.
      </div>
    );
  }

  const rightTabCls = (active: boolean) =>
    cn(
      'flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium -mb-px',
      active
        ? 'border-primary text-foreground'
        : 'border-transparent text-muted-foreground hover:text-foreground',
    );

  const isPromptMode = problem.execution_mode === 'prompt';

  const editorPanel = (
    <div className="flex h-full flex-col">
      {/* Right-panel tab strip: Code/Prompt | <submission verdict> */}
      <div className="flex items-center border-b border-border bg-muted/30 px-2 shrink-0">
        <button type="button" onClick={() => setRightView('code')} className={rightTabCls(rightView === 'code')}>
          {isPromptMode ? <Sparkles className="h-4 w-4" /> : <Code2 className="h-4 w-4" />}
          {isPromptMode ? 'Prompt' : 'Code'}
        </button>
        {openSub && (
          <div className="flex items-center">
            <button
              type="button"
              onClick={() => setRightView('submission')}
              className={rightTabCls(rightView === 'submission')}
            >
              <History className="h-3.5 w-3.5" />
              <span className={verdictColorClass(openSub.verdict)}>
                {VERDICT_LABEL[openSub.verdict] ?? openSub.verdict}
              </span>
            </button>
            <button
              type="button"
              onClick={closeSubmission}
              aria-label="Close submission"
              className="ml-0.5 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
        <button
          type="button"
          onClick={() => setTutorOpen((open) => !open)}
          className={cn('ml-auto', rightTabCls(tutorOpen))}
          aria-label="Ask Nudge"
        >
          <Sparkles className="h-4 w-4" />
          Ask Nudge
        </button>
      </div>

      {rightView === 'submission' && openSub ? (
        <div className="min-h-0 flex-1">
          <SubmissionDetail id={openSub.id} slug={slug} />
        </div>
      ) : isPromptMode ? (
        <div className="min-h-0 flex-1">
          <PromptEditorPanel
            problem={problem}
            value={code}
            onChange={(val) => setCode(slug, language, val)}
            onReset={() => setCode(slug, language, resolveStarter(problem, language))}
          />
        </div>
      ) : (
        <>
          <EditorToolbar
            slug={slug}
            problem={problem}
            languages={availableLanguages}
            isMaximized={maximized === 'editor'}
            onToggleMaximize={() => toggleMax('editor')}
            isCollapsed={editorCollapsed}
            onToggleCollapse={maximized ? undefined : () => togglePanel(editorRef.current)}
          />
          <div className="flex-1 overflow-hidden">
            <CodeEditor
              value={code}
              language={language}
              theme={theme}
              fontSize={fontSize}
              tabSize={tabSize}
              onChange={(val, ev) => {
                setCode(slug, language, val ?? '');
                telemetry.recordTyped(typedCharsFrom(ev?.changes));
              }}
              onCursorChange={(line, col) => setCursorPos({ line, col })}
              onPaste={telemetry.recordPaste}
            />
          </div>
          <EditorStatusBar line={cursorPos.line} column={cursorPos.col} />
        </>
      )}
    </div>
  );

  const mobileBody = (
      <Tabs
        value={mobileTab}
        onValueChange={(v) => setMobileTab(v as 'description' | 'code')}
        className="flex h-full flex-col"
      >
        <TabsList className="h-auto w-full justify-start rounded-none border-b border-border bg-transparent p-0 shrink-0">
          <TabsTrigger
            value="description"
            className="flex-1 rounded-none border-b-2 border-transparent py-2.5 text-sm data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none -mb-px"
          >
            Description
          </TabsTrigger>
          <TabsTrigger
            value="code"
            className="flex-1 rounded-none border-b-2 border-transparent py-2.5 text-sm data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none -mb-px"
          >
            Code
          </TabsTrigger>
        </TabsList>

        <TabsContent value="description" className="mt-0 flex-1 min-h-0">
          <LeftPanel
            problem={problem}
            slug={slug}
            onOpenSubmission={openSubmission}
            editorialUnlocked={editorialUnlocked}
            tab={leftTab}
            onTabChange={setLeftTab}
          />
        </TabsContent>

        <TabsContent value="code" className="mt-0 flex-1 min-h-0">
          <PanelGroup direction="vertical">
            <Panel defaultSize={60} minSize={30}>
              {editorPanel}
            </Panel>
            <PanelResizeHandle className="h-1 bg-border hover:bg-primary/50 transition-colors" />
            <Panel defaultSize={40} minSize={20}>
              <TestCasePanel
                testCases={testCases}
                result={result}
                isJudging={isJudging}
                mode={mode}
                executionMode={problem.execution_mode}
                signature={problem.signature}
                acceptedSlot={nextInSequenceEl}
              />
            </Panel>
          </PanelGroup>
        </TabsContent>
      </Tabs>
  );

  const cardCls = 'flex h-full flex-col overflow-hidden rounded-lg border border-border bg-card';

  const leftPanelEl = (
    <LeftPanel
      problem={problem}
      slug={slug}
      isMaximized={maximized === 'description'}
      onToggleMaximize={() => toggleMax('description')}
      onOpenSubmission={openSubmission}
      editorialUnlocked={editorialUnlocked}
      tab={leftTab}
      onTabChange={setLeftTab}
    />
  );

  const testcaseEl = (
    <TestCasePanel
      testCases={testCases}
      result={result}
      isJudging={isJudging}
      mode={mode}
      executionMode={problem.execution_mode}
      signature={problem.signature}
      isMaximized={maximized === 'testcase'}
      onToggleMaximize={() => toggleMax('testcase')}
      isCollapsed={testcaseCollapsed}
      onToggleCollapse={() => togglePanel(testcaseRef.current)}
      acceptedSlot={nextInSequenceEl}
    />
  );

  const desktopBody = maximized ? (
    // Single maximized pane fills the whole workspace.
    <div className="h-full p-2">
      <div className={cardCls}>
        {maximized === 'description' ? leftPanelEl : maximized === 'editor' ? editorPanel : testcaseEl}
      </div>
    </div>
  ) : (
    <PanelGroup direction="horizontal" className="h-full">
      {/* Left: Problem description */}
      <Panel defaultSize={tutorOpen ? 35 : 45} minSize={20}>
        <div className="h-full p-2">
          <div className={cardCls}>{leftPanelEl}</div>
        </div>
      </Panel>
      <PanelResizeHandle className="w-1 bg-border hover:bg-primary/50 transition-colors" />

      {/* Right: Editor + Test cases */}
      <Panel defaultSize={tutorOpen ? 40 : 55} minSize={25}>
        <div className="h-full p-2">
          <div className={cardCls}>
            <PanelGroup direction="vertical">
              <Panel
                ref={editorRef}
                collapsible
                collapsedSize={6}
                minSize={30}
                defaultSize={65}
                onCollapse={() => setEditorCollapsed(true)}
                onExpand={() => setEditorCollapsed(false)}
              >
                {editorPanel}
              </Panel>
              <PanelResizeHandle className="h-1 bg-border hover:bg-primary/50 transition-colors" />
              <Panel
                ref={testcaseRef}
                collapsible
                collapsedSize={6}
                minSize={15}
                defaultSize={35}
                onCollapse={() => setTestcaseCollapsed(true)}
                onExpand={() => setTestcaseCollapsed(false)}
              >
                {testcaseEl}
              </Panel>
            </PanelGroup>
          </div>
        </div>
      </Panel>

      {/*
        Matches LeetCode's own "Ask Leet" behavior: opening the tutor chat
        shifts and shrinks the existing panes to share space with it, as a
        real resizable pane in the same PanelGroup — it does not float on
        top of the editor as an overlay. (Mobile keeps the overlay/fixed
        presentation below, where a full-width sheet is the norm anyway.)
      */}
      {tutorOpen && (
        <>
          <PanelResizeHandle className="w-1 bg-border hover:bg-primary/50 transition-colors" />
          <Panel
            ref={tutorRef}
            collapsible
            collapsedSize={3}
            minSize={18}
            defaultSize={25}
            maxSize={45}
            onCollapse={() => setTutorCollapsed(true)}
            onExpand={() => setTutorCollapsed(false)}
          >
            <div className="h-full p-2 pl-0">
              <div className={cardCls}>
                <AskLeetsPanel
                  slug={slug}
                  code={code}
                  onClose={() => setTutorOpen(false)}
                  isCollapsed={tutorCollapsed}
                  onToggleCollapse={() => togglePanel(tutorRef.current)}
                />
              </div>
            </div>
          </Panel>
        </>
      )}
    </PanelGroup>
  );

  const workspaceBody = isMobile ? mobileBody : desktopBody;

  return (
    <div className="problem-workspace flex h-full flex-col">
      <ProblemTopBar
        slug={slug}
        onRun={handleRun}
        onSubmit={handleSubmit}
        isJudging={isJudging}
        isPromptMode={isPromptMode}
        topicId={topicId}
        batchId={batchId}
      />
      <div className="min-h-0 flex-1">{workspaceBody}</div>
      {/*
        Mounted once the problem is solved — in this session or an earlier one
        — so the question is never put to someone who has not solved it: the
        expected output is only their code's output if their code passes.

        Gating on `acceptedSubmit` ALONE was a real defect. Preparing a set
        takes over a minute, so a student who reloaded or moved to the next
        problem in that window lost the only mount that could ever display it;
        the questions finished generating server-side and became unreachable
        until they submitted again. Reproduced on local: a solved problem with
        three unanswered questions ready on the server rendered no dialog and
        issued no request at all.

        `solvedEver` mirrors what useEditorialAccess already does one screen
        up. Nothing is loosened by it — the server still refuses a caller with
        no accepted submission, and the dialog still renders null when there is
        nothing outstanding.
      */}
      {solvedEver && <ExplainSolveDialog slug={slug} justSolved={acceptedSubmit} />}
      {/*
        Fallback overlay for the two layouts that don't have room to shift a
        resizable pane in: mobile (single-column, a full-width sheet is the
        norm there) and the single-maximized-pane view. The normal desktop
        case mounts AskLeetsPanel as a real Panel inside desktopBody's
        PanelGroup instead, so it shrinks the other panes like LeetCode's own
        "Ask Leet" does, rather than floating on top of them. z-50 matches
        this app's own overlay convention (Dialog/Sheet/dropdown/popover all
        use z-50).
      */}
      {tutorOpen && (isMobile || maximized) && (
        <div className="fixed inset-y-0 right-0 z-50 w-full max-w-sm shadow-xl sm:w-96">
          <AskLeetsPanel slug={slug} code={code} onClose={() => setTutorOpen(false)} />
        </div>
      )}
    </div>
  );
}
